if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();

        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
          prev: this.querySelector('[data-pavera-gallery-prev]'),
          next: this.querySelector('[data-pavera-gallery-next]'),
          current: this.querySelector('[data-pavera-gallery-current]'),
          total: this.querySelector('[data-pavera-gallery-total]'),
        };

        this.mql = window.matchMedia('(min-width: 750px)');

        if (this.elements.viewer) {
          this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 120));

          this.elements.viewer.addEventListener(
            'click',
            (event) => {
              const imageOpener = event.target.closest('.product__modal-opener--image');
              if (!imageOpener) return;

              event.preventDefault();
              event.stopPropagation();
              event.stopImmediatePropagation();
              this.navigate(1);
            },
            true
          );
        }

        this.elements.prev?.addEventListener('click', (event) => {
          event.preventDefault();
          event.stopPropagation();
          this.navigate(-1);
        });

        this.elements.next?.addEventListener('click', (event) => {
          event.preventDefault();
          event.stopPropagation();
          this.navigate(1);
        });

        this.addEventListener('keydown', (event) => {
          if (event.key === 'ArrowLeft') {
            event.preventDefault();
            this.navigate(-1);
          }
          if (event.key === 'ArrowRight') {
            event.preventDefault();
            this.navigate(1);
          }
        });

        if (this.elements.thumbnails) {
          this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
            mediaToSwitch
              .querySelector('button')
              ?.addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
          });

          if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();
        }

        this.updateCounter();
        this.preloadAdjacent();
      }

      get mediaItems() {
        if (!this.elements.viewer) return [];
        return Array.from(this.elements.viewer.querySelectorAll('.product__media-list > li[data-media-id]'));
      }

      get activeIndex() {
        const items = this.mediaItems;
        const index = items.findIndex((item) => item.classList.contains('is-active'));
        return index >= 0 ? index : 0;
      }

      navigate(delta) {
        const items = this.mediaItems;
        if (items.length < 2) return;

        const targetIndex = (this.activeIndex + delta + items.length) % items.length;
        const target = items[targetIndex];
        this.setActiveMedia(target.dataset.mediaId, false);
      }

      onSlideChanged(event) {
        const currentElement = event.detail.currentElement;
        if (!currentElement) return;

        this.elements.viewer
          ?.querySelectorAll('.product__media-list > li[data-media-id]')
          .forEach((element) => element.classList.toggle('is-active', element === currentElement));

        if (this.elements.thumbnails) {
          const thumbnail = this.elements.thumbnails.querySelector(
            `[data-target="${currentElement.dataset.mediaId}"]`
          );
          this.setActiveThumbnail(thumbnail);
        }

        this.updateCounter();
        this.preloadAdjacent();
      }

      setActiveMedia(mediaId, prepend) {
        const activeMedia =
          this.elements.viewer?.querySelector(`.product__media-list > li[data-media-id="${mediaId}"]`) ||
          this.elements.viewer?.querySelector('.product__media-list > li[data-media-id]');

        if (!activeMedia) return;

        this.elements.viewer.querySelectorAll('.product__media-list > li[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia.classList.add('is-active');

        if (prepend) {
          activeMedia.parentElement.firstChild !== activeMedia && activeMedia.parentElement.prepend(activeMedia);

          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            if (activeThumbnail && activeThumbnail.parentElement.firstChild !== activeThumbnail) {
              activeThumbnail.parentElement.prepend(activeThumbnail);
            }
          }

          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        this.preventStickyHeader();

        window.setTimeout(() => {
          if (!this.mql.matches) {
            activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft, behavior: 'smooth' });
          }
        });

        this.playActiveMedia(activeMedia);

        if (this.elements.thumbnails) {
          const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
          this.setActiveThumbnail(activeThumbnail);
          if (activeThumbnail) this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
        }

        this.updateCounter();
        this.preloadAdjacent();
      }

      preloadAdjacent() {
        const items = this.mediaItems;
        if (items.length < 2) return;

        const current = this.activeIndex;
        const indexes = [
          (current + 1) % items.length,
          (current + 2) % items.length,
          (current - 1 + items.length) % items.length,
        ];

        indexes.forEach((index, position) => {
          const image = items[index]?.querySelector('.product__modal-opener--image img');
          if (!image) return;

          image.loading = 'eager';
          image.fetchPriority = position === 0 ? 'high' : 'low';

          if (!image.complete) {
            const preload = new Image();
            preload.decoding = 'async';
            preload.sizes = image.sizes || '';
            preload.srcset = image.srcset || '';
            preload.src = image.currentSrc || image.src;
          }
        });
      }

      updateCounter() {
        const items = this.mediaItems;
        if (!items.length) return;
        if (this.elements.current) this.elements.current.textContent = String(this.activeIndex + 1);
        if (this.elements.total) this.elements.total.textContent = String(items.length);
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));

        thumbnail.querySelector('button')?.setAttribute('aria-current', true);

        if (this.elements.thumbnails.isSlideVisible?.(thumbnail, 10)) return;
        this.elements.thumbnails.slider?.scrollTo({ left: thumbnail.offsetLeft, behavior: 'smooth' });
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image || !this.elements.liveRegion) return;

        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer?.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }
    }
  );
}
